import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class I18nTitleStrategy extends TitleStrategy {
  titleService: Title = inject(Title);
  translate: TranslateService = inject(TranslateService);

  updateTitle(snapshot: RouterStateSnapshot): void {
    const titleKey = this.buildTitle(snapshot);

    if (titleKey) {
      // Check if titleKey is a translation key by checking for known prefixes
      const translationKeyPrefixes = ['institution-portal.', 'student-portal.', 'alliant-portal.'];
      const isTranslationKey = translationKeyPrefixes.some((prefix) => titleKey.startsWith(prefix));

      if (!isTranslationKey) {
        // Plain string (e.g., institution name), set it directly
        this.titleService.setTitle(titleKey);
      } else {
        // Check for dynamic title params in route data
        const titleParams = this.getTitleParams(snapshot);
        const translateCall = this.translate.get(titleKey, titleParams);

        translateCall.subscribe((res: string) => {
          if (res && res !== titleKey) {
            this.titleService.setTitle(res);
          }
        });
      }
    }
  }

  /**
   * Extracts dynamic title parameters from the active route hierarchy.
   *
   * This method traverses through nested child routes to find and execute a `titleParams` function
   * that returns dynamic data (e.g., student name, course ID) to be used in title interpolation.
   * For example, a route could provide `{ studentName: 'John' }` to customize a title like
   * "Student Portal - John's Classes".
   *
   * @param snapshot - The current router state snapshot containing the active route hierarchy
   * @returns {Record<string, unknown> | undefined} An object containing title parameter values,
   *          or undefined if no titleParams function is found in any route
   */
  private getTitleParams(snapshot: RouterStateSnapshot): Record<string, unknown> | undefined {
    if (!snapshot.root) {
      return undefined;
    }
    let route = snapshot.root;
    while (route.firstChild) {
      route = route.firstChild;
      const titleParamsFn = route.data['titleParams'];
      if (typeof titleParamsFn === 'function') {
        return titleParamsFn(route);
      }
    }
    return undefined;
  }
}
